/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { motion, AnimatePresence } from 'framer-motion'; // Import motion and AnimatePresence
import SubContainer from '@/components/Container/SubContainer';
import { ArrowLeftCircleIcon, ArrowRightCircleIcon } from '@heroicons/react/24/outline';
import Divider from '@/components/Divider';
import { useEmotionCalendar } from './useEmotionCalendar';
import useIsMobile from '@/hooks/useIsMobile';
import { useRef, useState } from 'react';
import DialogEmotionDetail, { DialogEmotionDetailRef } from '@/pages/emotion-wheel/dialog-emotion-detail';
import { useEmotionWheelStore } from '@/pages/emotion-wheel/store/useEmotionWheelStore';
import { classNames } from '@/utils/class-names';

export const ProfileEmotionWheelTrackingCalendar = ({ isEmotionWheelPage }: { isEmotionWheelPage?: boolean }) => {
  const { currentMonth, days, handlePrevMonth, handleNextMonth, page } = useEmotionCalendar();
  const { emotionWheelpage } = useEmotionWheelStore();

  const { isMobile } = useIsMobile();
  const [emotionDetail, setEmotionDetail] = useState<any>({
    emoji: '',
    title: '',
    description: '',
  });

  const dialogRef = useRef<DialogEmotionDetailRef>(null);

  const onClickDay = (type: 'not-recorded' | 'recorded', data?: any) => {
    if (!isMobile) return;
    if (type === 'not-recorded') {
      setEmotionDetail({
        emoji: page?.emotionTrackingSection?.notRecordedIcon.data?.attributes?.url,
        title: page?.emotionTrackingSection?.notRecordedLabel,
        description: page?.emotionTrackingSection?.notRecordedDescription,
      });

      return dialogRef.current?.openDialog();
    }

    setEmotionDetail({
      emoji: data?.emoji,
      title: data?.label,
      description: data?.description,
    });

    return dialogRef.current?.openDialog();
  };

  const renderHeader = () => (
    <div className="mb-4 flex items-center justify-center gap-3">
      <button className="text-black hover:text-gray-500" onClick={handlePrevMonth}>
        <ArrowLeftCircleIcon className="h-6 w-6" />
      </button>
      <h2 className="text-lg font-semibold">{currentMonth.format('MMMM YYYY')}</h2>
      <button className="text-black hover:text-gray-500" onClick={handleNextMonth}>
        <ArrowRightCircleIcon className="h-6 w-6" />
      </button>
    </div>
  );

  return (
    <SubContainer className="lg:!col-start-3 lg:!col-end-11">
      {!isEmotionWheelPage && <h1 className="text-sh-md font-bold">Emotion Tracking</h1>}
      {isEmotionWheelPage && (
        <h1 className=" text-center font-larken text-sh-20-28 ">{emotionWheelpage?.calendarTitle}</h1>
      )}
      <div className="mt-5 rounded-2xl border border-primary-500 bg-white p-5">
        {renderHeader()}
        <Divider />
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={currentMonth.format('MMMM YYYY')}
            initial={{ opacity: 1 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-7 bg-white text-center text-gray-700">
            {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((d) => (
              <div key={d} className="py-1 font-bold">
                {d}
              </div>
            ))}
            {days.length > 0 &&
              days.map(({ day, emoji, isDisabledTracking, isCurrentMonth, label, ...rest }, index) => {
                const isToday = day === new Date().getDate() && currentMonth.isSame(new Date(), 'month');
                return (
                  <motion.div
                    key={index}
                    className={`flex flex-col items-center justify-center ${isCurrentMonth ? 'text-black' : 'text-gray-400'}`}
                    style={{ opacity: isCurrentMonth ? 1 : 0.5 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.01 }}>
                    <div className="text-sm font-medium">{day}</div>
                    {!isDisabledTracking &&
                      page?.emotionTrackingSection?.notRecordedIcon.data?.attributes?.url &&
                      emoji && (
                        <img
                          className={classNames(
                            'mb-5 mt-sh-5 size-8 md:mb-0 md:mt-sh-15  md:size-12 ',
                            isToday ? 'rounded-full border-2 border-[#1775E3] p-0.5' : '',
                          )}
                          src={emoji}
                          alt="emotion"
                          onClick={() =>
                            onClickDay('recorded', { day, emoji, isDisabledTracking, isCurrentMonth, label, ...rest })
                          }
                          style={{ opacity: !isCurrentMonth ? '0.5' : '' }}
                        />
                      )}
                    {!isDisabledTracking &&
                      !emoji &&
                      page?.emotionTrackingSection?.notRecordedIcon.data?.attributes?.url && (
                        <img
                          className={classNames(
                            'mb-5 mt-sh-5 size-8 md:mb-0 md:mt-sh-15 md:size-12 ',
                            isToday ? 'rounded-full border-2 border-[#1775E3] p-0.5' : '',
                          )}
                          src={page?.emotionTrackingSection?.notRecordedIcon.data?.attributes?.url || ''}
                          alt="not-emotion"
                          onClick={() => onClickDay('not-recorded')}
                          style={{ opacity: !isCurrentMonth ? '0.5' : '' }}
                        />
                      )}
                    {/* {isDisabledTracking && (
              <img
                className="mt-sh-15 size-8 md:size-12"
                src={page?.emotionTrackingSection?.notTrackingIcon.data?.attributes?.url || ''}
                alt="not-emotion"
                style={{ opacity: !isCurrentMonth ? '0.5' : '' }}
              />
            )} */}
                    {emoji && !isMobile && <p className=" mb-4 mt-2 text-sh-xxs font-medium ">{label}</p>}
                    {!emoji && !isMobile && <p className=" mb-7 mt-sh-15 text-sh-xxs font-medium ">{label}</p>}
                  </motion.div>
                );
              })}
            {days.length === 0 &&
              Array.from({ length: 42 - days.length }).map((_, index) => (
                <div className="m-2 flex flex-col items-center justify-center" key={index}>
                  <div className="h-4 w-1/4 animate-pulse bg-gray-300 text-sm font-medium"></div>
                  <div key={index} className="flex justify-center">
                    <div className="mt-6 size-8 animate-pulse rounded-full  border-4 border-dashed border-gray-300 md:size-14"></div>
                  </div>
                  <p className=" mb-4 mt-2 h-4 w-full animate-pulse bg-gray-300 text-sh-xxs font-medium"></p>
                </div>
              ))}
          </motion.div>
        </AnimatePresence>
      </div>
      {isEmotionWheelPage && (
        <div
          className="mt-sh-30 text-center [&>p]:text-sh-xxs"
          dangerouslySetInnerHTML={{
            __html: emotionWheelpage?.bottomText || '',
          }}
        />
      )}
      {/* <Button className="mt-5" onClick={onChangeEmotionTrack} loading={isLoading}>
        {disableEmotionTracking
          ? page?.emotionTrackingSection?.btnStartTrackingLabel
          : page?.emotionTrackingSection?.btnStopTrackingLabel}
      </Button> */}
      <DialogEmotionDetail ref={dialogRef} detail={emotionDetail} />
    </SubContainer>
  );
};
